import { useEffect, useState } from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	TextField,
	Typography,
} from "@mui/material";
import {
	LABEL_CATEGORIA_INTELIGENTE,
	LABEL_SEVERIDADE,
	SEVERIDADES,
} from "../../../../utils/centralDeAlertas/labels";

// Modal de edição dos parâmetros de uma detecção inteligente (aberto pelo
// menu do card da aba Inteligentes). Só as categorias com parâmetros chegam
// aqui (zona_morta e falha_recorrente — os_faltante é binária e o menu nem
// oferece a opção). Só fecha quando o backend aceitou (onSalvar devolve
// true/false, mesmo contrato do modalTipoAlerta).

function inteiroValido(valor, { min, max }) {
	const numero = Number(valor);
	return (
		valor !== "" && Number.isInteger(numero) && numero >= min && numero <= max
	);
}

export default function ModalCategoriaInteligente({
	open,
	onClose,
	categoria,
	limites,
	janelaCorrelacaoMinutos,
	onSalvar,
}) {
	const chave = categoria?.chave;

	// zona_morta: string vazia = sem limiar próprio (segue a janela de correlação).
	const [limiar, setLimiar] = useState("");
	// falha_recorrente.
	const [minimo, setMinimo] = useState("");
	const [janelaHoras, setJanelaHoras] = useState("");
	const [severidade, setSeveridade] = useState("alto");
	const [salvando, setSalvando] = useState(false);

	// Recarrega o formulário com os valores salvos sempre que reabrir.
	useEffect(() => {
		if (open && categoria) {
			setLimiar(
				categoria.limiarMinutos != null ? String(categoria.limiarMinutos) : ""
			);
			setMinimo(
				categoria.minimoAlertas != null ? String(categoria.minimoAlertas) : ""
			);
			setJanelaHoras(
				categoria.janelaHoras != null ? String(categoria.janelaHoras) : ""
			);
			setSeveridade(categoria.severidadeMinima || "alto");
			setSalvando(false);
		}
	}, [open, categoria]);

	if (!categoria) return null;

	// O limiar da zona morta nunca fica abaixo da janela de correlação vigente
	// (mesma validação do backend); vazio = segue a janela.
	const limitesLimiar = {
		min: Math.max(
			limites?.limiarMinutos?.min ?? 1,
			janelaCorrelacaoMinutos || 1
		),
		max: limites?.limiarMinutos?.max ?? 1440,
	};
	const limitesMinimo = limites?.minimoAlertas || { min: 2, max: 50 };
	const limitesJanelaHoras = limites?.janelaHoras || { min: 1, max: 168 };

	const limiarValido = limiar === "" || inteiroValido(limiar, limitesLimiar);
	const minimoValido = inteiroValido(minimo, limitesMinimo);
	const janelaHorasValida = inteiroValido(janelaHoras, limitesJanelaHoras);

	const podeSalvar =
		chave === "zona_morta" ? limiarValido : minimoValido && janelaHorasValida;

	const handleSalvar = async () => {
		setSalvando(true);
		try {
			const payloadCampos =
				chave === "zona_morta"
					? { limiarMinutos: limiar === "" ? null : Number(limiar) }
					: {
							minimoAlertas: Number(minimo),
							janelaHoras: Number(janelaHoras),
							severidadeMinima: severidade,
					  };
			// A ativação é do menu do card — o modal preserva o estado salvo.
			const ok = await onSalvar({
				chave,
				ativo: Boolean(categoria.ativo),
				...payloadCampos,
			});
			if (ok) onClose();
		} finally {
			setSalvando(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
			<DialogTitle>
				{`Editar ${LABEL_CATEGORIA_INTELIGENTE[chave] || chave}`}
			</DialogTitle>
			<DialogContent>
				{chave === "zona_morta" && (
					<>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							Alerta sem nenhum atendimento correlacionado por mais que o
							limiar entra nos Alertas inteligentes como zona morta.
						</Typography>
						<TextField
							size="small"
							type="number"
							fullWidth
							label="Limiar (minutos)"
							value={limiar}
							onChange={(evento) => setLimiar(evento.target.value)}
							inputProps={{
								min: limitesLimiar.min,
								max: limitesLimiar.max,
								step: 1,
							}}
							error={!limiarValido}
							helperText={
								!limiarValido
									? `Use um inteiro entre ${limitesLimiar.min} e ${limitesLimiar.max}, ou deixe vazio`
									: `Vazio = janela de correlação (${janelaCorrelacaoMinutos} min)`
							}
							sx={{ mt: 1 }}
						/>
					</>
				)}

				{chave === "falha_recorrente" && (
					<>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							Equipamento com N ou mais alertas de indisponibilidade em
							qualquer janela deslizante de X horas vira falha recorrente.
						</Typography>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
							<TextField
								size="small"
								type="number"
								label="Mínimo de alertas"
								value={minimo}
								onChange={(evento) => setMinimo(evento.target.value)}
								inputProps={{
									min: limitesMinimo.min,
									max: limitesMinimo.max,
									step: 1,
								}}
								error={minimo !== "" && !minimoValido}
								helperText={
									minimo !== "" && !minimoValido
										? `Use um inteiro entre ${limitesMinimo.min} e ${limitesMinimo.max}`
										: ""
								}
							/>
							<TextField
								size="small"
								type="number"
								label="Janela (horas)"
								value={janelaHoras}
								onChange={(evento) => setJanelaHoras(evento.target.value)}
								inputProps={{
									min: limitesJanelaHoras.min,
									max: limitesJanelaHoras.max,
									step: 1,
								}}
								error={janelaHoras !== "" && !janelaHorasValida}
								helperText={
									janelaHoras !== "" && !janelaHorasValida
										? `Use um inteiro entre ${limitesJanelaHoras.min} e ${limitesJanelaHoras.max}`
										: ""
								}
							/>
							<TextField
								size="small"
								select
								label="Severidade mínima"
								value={severidade}
								onChange={(evento) => setSeveridade(evento.target.value)}
								helperText="Alertas com essa severidade ou pior contam como indisponibilidade"
							>
								{SEVERIDADES.map((valor) => (
									<MenuItem key={valor} value={valor}>
										{LABEL_SEVERIDADE[valor]}
									</MenuItem>
								))}
							</TextField>
							<Typography variant="caption" color="text.secondary">
								Alertas do tipo “Host fora do ar” (ICMP/SNMP) contam sempre, em
								qualquer severidade.
							</Typography>
						</Box>
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={salvando}>
					Cancelar
				</Button>
				<Button
					variant="contained"
					onClick={handleSalvar}
					disabled={!podeSalvar || salvando}
				>
					{salvando ? "Salvando…" : "Salvar"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
